import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
  useLazyGetUsersListQuery,
  useLazyGetLeadsQuery,
  useLazyGetServicesQuery,
  useLazyGetTasksQuery,
  useLazyGetTaskTemplatesQuery,
  useLazyGetTaskTemplateByIdQuery,
  useLazyGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import {
  PRIORITY_OPTIONS,
  formatTaskDeadlineForApi,
  formatTaskDeadlineForInput,
  userLabel,
  leadLabel,
  serviceLabel,
} from './taskApiHelpers';
import FutureDatetimeTextField from '../../components/forms/FutureDatetimeTextField';
import { nowDatetimeLocal, clampDatetimeLocal } from '../../utils/datetimeLocal';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('responsible_id', String(values.responsible_id));
  fd.append('lead_id', String(values.lead_id));
  fd.append('service_id', String(values.service_id));
  if (values.parent_id) {
    fd.append('parent_id', String(values.parent_id));
  }
  fd.append('title', values.title ?? '');
  fd.append('priority', values.priority ?? '');
  if (values.deadline) {
    fd.append('deadline', formatTaskDeadlineForApi(values.deadline));
  }
  fd.append('description', values.description ?? '');
  fd.append('save_as_template', values.save_as_template ? '1' : '0');

  values.assignees.forEach((user, idx) => {
    fd.append(`assignees[${idx}][id]`, String(user.id));
    fd.append(`assignees[${idx}][role]`, 'assignee');
  });

  values.observers.forEach((user, idx) => {
    fd.append(`observers[${idx}][id]`, String(user.id));
    fd.append(`observers[${idx}][role]`, 'observer');
  });

  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const mapUserOption = (user) => ({
  id: user?.id ?? user?.uuid,
  label: userLabel(user),
  raw: user,
});

const mapLeadOption = (lead) => ({
  id: lead?.id,
  label: leadLabel(lead),
  raw: lead,
});

const mapServiceOption = (service) => ({
  id: service?.id,
  label: serviceLabel(service),
  raw: service,
});

const mapTaskOption = (task) => ({
  id: task?.id,
  label: task?.title ?? String(task?.id ?? ''),
  raw: task,
});

const ensureOption = (list, option) => {
  if (!option?.id) return list;
  return list.some((item) => item.id === option.id) ? list : [...list, option];
};

const INTERNAL_LEAD_NAME = 'Internal Lead';

const findInternalLeadOption = (options) =>
  options.find((o) => {
    const label = String(o.label ?? '').trim().toLowerCase();
    const name = String(o.raw?.name ?? '').trim().toLowerCase();
    const target = INTERNAL_LEAD_NAME.toLowerCase();
    return label === target || name === target;
  }) ?? null;

const CreateTask = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetailLoading, setTemplateDetailLoading] = useState(false);
  const templateFetchSeq = useRef(0);
  const defaultLeadApplied = useRef(false);
  const defaultStatusApplied = useRef(false);
  const hydratedRecordId = useRef(null);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedObservers, setSelectedObservers] = useState([]);

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [deadline, setDeadline] = useState('');
  const [description, setDescription] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  const [record, setRecord] = useState(null);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors({ assignees_0: 'assignees', observers_0: 'observers' });
  const [minDeadlineDatetime] = useState(() => nowDatetimeLocal());

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatusesList] = useLazyGetStatusesQuery();
  const [fetchUsers] = useLazyGetUsersListQuery();
  const [fetchLeads] = useLazyGetLeadsQuery();
  const [fetchServices] = useLazyGetServicesQuery();
  const [fetchTasks] = useLazyGetTasksQuery();
  const [fetchTaskTemplates] = useLazyGetTaskTemplatesQuery();
  const [fetchTaskTemplateById] = useLazyGetTaskTemplateByIdQuery();
  const [fetchTaskById] = useLazyGetTaskByIdQuery();
  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();

  const fetchTaskStatuses = useCallback(async () => {
    try {
      return await fetchStatusesByModel('task').unwrap();
    } catch {
      try {
        return await fetchStatusesByModel('Task').unwrap();
      } catch {
        return await fetchStatusesList({ paginate: 0 }).unwrap();
      }
    }
  }, [fetchStatusesByModel, fetchStatusesList]);

  const statusLazyTrigger = useCallback(
    () => ({
      unwrap: () => fetchTaskStatuses(),
    }),
    [fetchTaskStatuses]
  );

  const statusSelect = useLazySelectOptions(statusLazyTrigger, {
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const userSelect = useLazySelectOptions(fetchUsers, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map(mapUserOption).filter((u) => u.id);
    },
  });

  const leadSelect = useLazySelectOptions(fetchLeads, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map(mapLeadOption).filter((l) => l.id);
    },
  });

  const serviceSelect = useLazySelectOptions(fetchServices, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map(mapServiceOption).filter((s) => s.id);
    },
  });

  const parentSelect = useLazySelectOptions(fetchTasks, {
    // Options show the task title only — no relations needed
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : [])
        .filter((task) => task?.id && String(task.id) !== String(editId))
        .map(mapTaskOption);
    },
  });

  const templateSelect = useLazySelectOptions(fetchTaskTemplates, {
    enabled: !isEdit,
    // Options show the template title only — no relations needed
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      const list = Array.isArray(arr) ? arr : [];
      return list
        .filter((t) => t?.id)
        .map((t) => ({
          id: t.id,
          label: t.title?.trim() ? String(t.title) : `Template #${t.id}`,
          raw: t,
        }));
    },
  });

  const loadStatusOptions = useCallback(() => {
    return statusSelect.load().catch(() => {
      toast.error('Could not load task statuses');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [statusSelect.load]);

  const loadUserOptions = useCallback(() => {
    return userSelect.load().catch(() => {
      toast.error('Could not load users');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [userSelect.load]);

  const loadLeadOptions = useCallback(() => {
    return leadSelect.load().catch(() => {
      toast.error('Could not load leads');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [leadSelect.load]);

  const loadServiceOptions = useCallback(() => {
    return serviceSelect.load().catch(() => {
      toast.error('Could not load services');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [serviceSelect.load]);

  const loadParentOptions = useCallback(() => {
    return parentSelect.load().catch(() => {
      toast.warn('Could not load parent tasks');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [parentSelect.load]);

  const loadTemplateOptions = useCallback(() => {
    return templateSelect.load().catch(() => {
      toast.warn('Could not load task templates');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [templateSelect.load]);

  useEffect(() => {
    if (!isEdit) {
      setDeadline((prev) => prev || nowDatetimeLocal());
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      defaultLeadApplied.current = false;
      defaultStatusApplied.current = false;
      return;
    }
    if (defaultStatusApplied.current) return;
    let cancelled = false;
    loadStatusOptions().then((opts) => {
      if (cancelled || defaultStatusApplied.current || !opts?.length) return;
      const pending = opts.find((s) => String(s.label || '').toLowerCase() === 'pending');
      if (pending) {
        setSelectedStatus(pending);
        defaultStatusApplied.current = true;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, loadStatusOptions]);

  useEffect(() => {
    if (isEdit || defaultLeadApplied.current) return;
    let cancelled = false;
    loadLeadOptions().then((opts) => {
      if (cancelled || defaultLeadApplied.current) return;
      const internalLead = findInternalLeadOption(opts || []);
      if (internalLead) {
        setSelectedLead(internalLead);
        defaultLeadApplied.current = true;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, loadLeadOptions]);

  const applyTaskTemplateRecord = useCallback((templateRecord) => {
    if (!templateRecord) return;
    setTitle(templateRecord.title ?? '');
    setPriority(String(templateRecord.priority || 'medium').toLowerCase() || 'medium');
    setDescription(templateRecord.description ?? '');
  }, []);

  const handleTemplateChange = useCallback(
    async (_, option) => {
      setSelectedTemplate(option);
      if (!option?.id) return;
      const seq = ++templateFetchSeq.current;
      setTemplateDetailLoading(true);
      try {
        // Only title / priority / description are copied over — no relations needed
        const data = await fetchTaskTemplateById({ id: option.id }).unwrap();
        if (seq !== templateFetchSeq.current) return;
        const templateRecord = data?.data ?? data;
        if (templateRecord) applyTaskTemplateRecord(templateRecord);
      } catch (err) {
        if (seq === templateFetchSeq.current) {
          toast.error(err?.data?.message || 'Failed to load task template');
        }
      } finally {
        if (seq === templateFetchSeq.current) setTemplateDetailLoading(false);
      }
    },
    [applyTaskTemplateRecord, fetchTaskTemplateById]
  );

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setRecord(null);
      hydratedRecordId.current = null;
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const data = await fetchTaskById(editId).unwrap();
        const payload = data?.data ?? data;
        if (!cancelled) setRecord(payload);
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to load task');
        navigate('/tasks');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId, isEdit, navigate, fetchTaskById]);

  useEffect(() => {
    if (!isEdit || !record?.id) return;
    if (hydratedRecordId.current === record.id) return;
    hydratedRecordId.current = record.id;

    setTitle(record.title ?? '');
    setPriority(record.priority ?? 'medium');
    setDeadline(
      clampDatetimeLocal(
        formatTaskDeadlineForInput(record.deadline) || nowDatetimeLocal(),
        minDeadlineDatetime
      )
    );
    setDescription(record.description ?? '');
    setSaveAsTemplate(
      record.save_as_template === true ||
        record.save_as_template === 1 ||
        String(record.save_as_template ?? '') === '1'
    );

    const nestedStatus = record.status && typeof record.status === 'object' ? record.status : null;
    const statusId = record.status_id ?? nestedStatus?.id;
    if (statusId) {
      const option = nestedStatus
        ? { id: statusId, label: statusLabel(nestedStatus), raw: nestedStatus }
        : { id: statusId, label: String(statusId), raw: {} };
      setSelectedStatus(option);
      statusSelect.setOptions((prev) => ensureOption(prev, option));
    }

    const nestedResponsible =
      record.responsible && typeof record.responsible === 'object' ? record.responsible : null;
    const responsibleId = record.responsible_id ?? nestedResponsible?.id;
    if (responsibleId) {
      const option = nestedResponsible
        ? mapUserOption(nestedResponsible)
        : { id: responsibleId, label: String(responsibleId), raw: {} };
      setSelectedResponsible(option);
      userSelect.setOptions((prev) => ensureOption(prev, option));
    }

    const nestedLead = record.lead && typeof record.lead === 'object' ? record.lead : null;
    const leadId = record.lead_id ?? nestedLead?.id;
    if (leadId) {
      const option = nestedLead
        ? mapLeadOption(nestedLead)
        : { id: leadId, label: String(leadId), raw: {} };
      setSelectedLead(option);
      leadSelect.setOptions((prev) => ensureOption(prev, option));
    }

    const nestedService = record.service && typeof record.service === 'object' ? record.service : null;
    const serviceId = record.service_id ?? nestedService?.id;
    if (serviceId) {
      const option = nestedService
        ? mapServiceOption(nestedService)
        : { id: serviceId, label: String(serviceId), raw: {} };
      setSelectedService(option);
      serviceSelect.setOptions((prev) => ensureOption(prev, option));
    }

    const nestedParent = record.parent && typeof record.parent === 'object' ? record.parent : null;
    const parentId = record.parent_id ?? nestedParent?.id;
    if (parentId) {
      const option = nestedParent
        ? mapTaskOption(nestedParent)
        : { id: parentId, label: String(parentId), raw: {} };
      setSelectedParent(option);
      parentSelect.setOptions((prev) => ensureOption(prev, option));
    } else {
      setSelectedParent(null);
    }

    const assignees = Array.isArray(record.assignees) ? record.assignees : [];
    const mappedAssignees = assignees.map(mapUserOption).filter((u) => u.id);
    setSelectedAssignees(mappedAssignees);
    if (mappedAssignees.length) {
      userSelect.setOptions((prev) =>
        mappedAssignees.reduce((acc, option) => ensureOption(acc, option), prev)
      );
    }

    const observers = Array.isArray(record.observers) ? record.observers : [];
    const mappedObservers = observers.map(mapUserOption).filter((u) => u.id);
    setSelectedObservers(mappedObservers);
    if (mappedObservers.length) {
      userSelect.setOptions((prev) =>
        mappedObservers.reduce((acc, option) => ensureOption(acc, option), prev)
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [
    isEdit,
    record,
    statusSelect.setOptions,
    userSelect.setOptions,
    leadSelect.setOptions,
    serviceSelect.setOptions,
    parentSelect.setOptions,
    minDeadlineDatetime,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = {};
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    if (!selectedResponsible?.id) clientErrors.responsible_id = 'Responsible is required';
    if (!selectedLead?.id) clientErrors.lead_id = 'Lead is required';
    if (!selectedService?.id) clientErrors.service_id = 'Service is required';
    if (!title.trim()) clientErrors.title = 'Title is required';
    if (!deadline) clientErrors.deadline = 'Deadline is required';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      responsible_id: selectedResponsible.id,
      lead_id: selectedLead.id,
      service_id: selectedService.id,
      parent_id: selectedParent?.id ?? '',
      assignees: selectedAssignees,
      observers: selectedObservers,
      title: title.trim(),
      priority,
      deadline,
      description: description.trim(),
      save_as_template: saveAsTemplate,
    };

    const fd = buildFormData(values);

    setSubmitting(true);
    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateTask({ id: editId, data: fd }).unwrap();
        toast.success('Task updated');
      } else {
        await createTask({ data: fd }).unwrap();
        toast.success('Task added');
      }
      navigate('/tasks');
    } catch (err) {
      applyApiError(err, isEdit ? 'Failed to update task' : 'Failed to add task');
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = statusSelect.options;
  const userOptions = userSelect.options;
  const leadOptions = leadSelect.options;
  const serviceOptions = serviceSelect.options;
  const parentOptions = parentSelect.options;
  const templateOptions = templateSelect.options;

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')} sx={{ mb: 2 }}>
          Back to tasks
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task' : 'Add task'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {!isEdit ? (
                <Grid item xs={12}>
                  <Autocomplete
                    options={templateOptions}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    onOpen={() => loadTemplateOptions()}
                    loading={templateSelect.loading || templateDetailLoading}
                    getOptionLabel={(opt) => (opt?.label != null ? String(opt.label) : '')}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Task template"
                        placeholder="Optional — prefills title, priority, and description"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}

              {isEdit ? (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={(_, v) => {
                      setSelectedStatus(v);
                      clearError('status_id');
                    }}
                    onOpen={() => loadStatusOptions()}
                    loading={statusSelect.loading}
                    getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        required
                        fullWidth
                        {...fieldProps('status_id')}
                      />
                    )}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={userOptions}
                  value={selectedResponsible}
                  onChange={(_, v) => {
                    setSelectedResponsible(v);
                    clearError('responsible_id');
                  }}
                  onOpen={() => loadUserOptions()}
                  loading={userSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsible"
                      required
                      fullWidth
                      {...fieldProps('responsible_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={leadOptions}
                  value={selectedLead}
                  onChange={(_, v) => {
                    setSelectedLead(v);
                    clearError('lead_id');
                  }}
                  onOpen={() => loadLeadOptions()}
                  loading={leadSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lead"
                      required
                      fullWidth
                      {...fieldProps('lead_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={serviceOptions}
                  value={selectedService}
                  onChange={(_, v) => {
                    setSelectedService(v);
                    clearError('service_id');
                  }}
                  onOpen={() => loadServiceOptions()}
                  loading={serviceSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Service"
                      required
                      fullWidth
                      {...fieldProps('service_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={parentOptions}
                  value={selectedParent}
                  onChange={(_, v) => {
                    setSelectedParent(v);
                    clearError('parent_id');
                  }}
                  onOpen={() => loadParentOptions()}
                  loading={parentSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parent task"
                      placeholder="Optional"
                      fullWidth
                      {...fieldProps('parent_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Priority"
                  value={priority}
                  onChange={(e) => {
                    setPriority(e.target.value);
                    clearError('priority');
                  }}
                  fullWidth
                  {...fieldProps('priority')}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearError('title');
                  }}
                  required
                  fullWidth
                  {...fieldProps('title')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedAssignees}
                  onChange={(_, v) => {
                    setSelectedAssignees(v);
                    clearError('assignees');
                  }}
                  onOpen={() => loadUserOptions()}
                  loading={userSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Assignees" fullWidth {...fieldProps('assignees')} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedObservers}
                  onChange={(_, v) => {
                    setSelectedObservers(v);
                    clearError('observers');
                  }}
                  onOpen={() => loadUserOptions()}
                  loading={userSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Observers" fullWidth {...fieldProps('observers')} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FutureDatetimeTextField
                  label="Deadline"
                  value={deadline}
                  onChange={(v) => {
                    setDeadline(v);
                    clearError('deadline');
                  }}
                  minDatetime={minDeadlineDatetime}
                  required
                  fullWidth
                  {...fieldProps('deadline')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveAsTemplate}
                      onChange={(e) => setSaveAsTemplate(e.target.checked)}
                    />
                  }
                  label="Save as template"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError('description');
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps('description')}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add task'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/tasks')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTask;
